import { useTranslation } from 'react-i18next';
import type { SimpleFunc } from '@bluevision/tools';
import { Form } from 'antd';
import { Modal, Spacing, Message } from '@bluevision/components';
import { trimSpace } from '@/utils';
import SmsCodeFormItem from '@/pages/Administration/components/SmsCodeFormItem';
import { useState } from 'react';
import styles from './index.less';
import { Input, Button } from '@bluevision/components';
import { useModel } from 'umi';
import { isValue, toUnderline } from '@bluevision/tools';
import { useRequest } from 'ahooks';
import type { UserAPI } from '@/services/authority/interface';
import { modifyUserInfo } from '@/services/authority/login';

interface IMyInformationProps {
  onClose: SimpleFunc;
}

type IFormValues = UserAPI.Request.ModifyUserInfoParams;

const MyInformation = ({ onClose }: IMyInformationProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(true);
  const [showSave, setShowSave] = useState(false);
  const [form] = Form.useForm();

  const { initialState, setInitialState } = useModel('@@initialState');
  const userInfo = initialState?.currentUser?.userInfo;

  const handleFormValuesChange = (changedValues: Partial<IFormValues>, allValues: IFormValues) => {
    const allFieldsFilled = Object.values(allValues).every((value) => isValue(value));
    setShowSave(allFieldsFilled);
  };

  const { loading, runAsync } = useRequest(modifyUserInfo, { manual: true });
  const onSubmit = async (values: IFormValues) => {
    const [, error] = await runAsync(values);
    if (!error) {
      setShow(false);
      Message.success(t('modified_successfully'));

      const fetchUserInfo = await initialState?.fetchUserInfo?.();
      if (fetchUserInfo) {
        await setInitialState((s) => ({
          ...s,
          currentUser: fetchUserInfo,
        }));
      }
    }
  };

  return (
    <Modal
      open={show}
      title={t('my_information')}
      onCancel={() => setShow(false)}
      afterClose={onClose}
      footer={null}
      maskClosable={false}
      size='small'
    >
      <Form
        layout='vertical'
        onFinish={onSubmit}
        form={form}
        onValuesChange={handleFormValuesChange}
        initialValues={toUnderline(userInfo)}
      >
        <Form.Item
          label={t('username')}
          name='contact_name'
          getValueFromEvent={trimSpace}
          rules={[
            {
              required: true,
              message: t('please_enter_the_user_name'),
            },
          ]}
        >
          <Input size='large' placeholder={t('please_enter_the_user_name')} />
        </Form.Item>
        <Form.Item
          label={t('mobile_phone_number')}
          name='phone'
          getValueFromEvent={trimSpace}
          rules={[
            {
              required: true,
              message: t('please_enter_your_phone_or_phone_number'),
            },
            {
              pattern: /^1[3-9]\d{9}$/,
              message: t('please_enter_your_phone_or_phone_number'),
            },
          ]}
        >
          <Input size='large' placeholder={t('please_enter_your_phone_or_phone_number')} />
        </Form.Item>
        <Form.Item
          label={t('verification_code')}
          name='verify_code'
          getValueFromEvent={trimSpace}
          rules={[
            {
              required: true,
              message: t('please_enter_the_verification_code'),
            },
          ]}
        >
          <SmsCodeFormItem form={form} verify_type='modify' dependency='phone' />
        </Form.Item>
        <Form.Item className={styles.buttonGroup}>
          <Spacing>
            <Button onClick={() => setShow(false)}>{t('cancel')}</Button>
            <Button loading={loading} type='primary' htmlType='submit' disabled={!showSave}>
              {t('save')}
            </Button>
          </Spacing>
        </Form.Item>
      </Form>
    </Modal>
  );
};
export default MyInformation;
